import { useState, useEffect } from "react";
import { Download, Package, Shield, Grid3X3, CheckCircle, Settings } from "lucide-react";
import { DeviceSidebar } from "./DeviceSidebar";
import { DeviceAppsContent } from "./DeviceAppsContent";
import { DeviceAppsCategoriesContent } from "./DeviceAppsCategoriesContent";
import { DeviceAppsSettingsContent } from "./DeviceAppsSettingsContent";
import { useDeviceLevelData } from "../hooks/useDeviceLevelData";
import { useDeviceAppMode } from "@/hooks/useDeviceAppMode";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";
import { useQuery } from "convex/react";
import { api } from "../../api";
import type { AppMode } from "@/types/convex";
import { InstalledAppsReviewModal, type InstalledAppMeta } from "./InstalledAppsReviewModal";

const appsSidebarItems = [
  { id: "settings", label: "Settings", icon: Settings },
  { id: "categories", label: "App Categories", icon: Grid3X3 },
  { id: "whitelist", label: "App Whitelist", icon: CheckCircle },
  { id: "blacklist", label: "App Blacklist", icon: Shield },
  { id: "available", label: "All Available Apps", icon: Download },
  { id: "installed", label: "Installed Apps", icon: Package },
];

interface DeviceAppsTabProps {
  deviceId?: string;
}

export const DeviceAppsTab = ({ deviceId }: DeviceAppsTabProps) => {
  const [activeAppsSection, setActiveAppsSection] = useState("settings");
  const [showWhitelistImportModal, setShowWhitelistImportModal] = useState(false);
  const { level } = useDeviceLevelData(deviceId || '');
  
  // Use the new mode hook for explicit mode management
  const { appMode, setAppMode, setAppModeToCategoriesWithPrune, setAppModeToWhitelistWithImport, deviceWhitelistedAppIds, isLoading } = useDeviceAppMode(deviceId || '');

  // Fetch whitelisted apps with category info (only when in whitelist mode, for pruning on switch)
  const whitelistedAppsWithCategory = useQuery(
    api.apps.get.apps,
    appMode === "whitelist" && deviceWhitelistedAppIds.length > 0
      ? { apps: deviceWhitelistedAppIds as any[] }
      : "skip"
  );

  // We also need to fetch category for each whitelisted app.
  // apps.get.apps doesn't return category, so we use apps.list.all with individual bundleId queries.
  // Since we can't call hooks in a loop, we'll look up via allowedByDeviceStatic if serial is available.
  // Alternatively, we'll search apps.list.all for each bundleId once we have the bundleIds.
  // For now, we fetch all app details and build an ID->category map using the allowedByDeviceStatic API.

  // Build appId->category map from whitelisted apps data
  // We'll query apps.list.all with bundleIds to get categories
  const bundleIdList = whitelistedAppsWithCategory?.map(a => a.bundleId).filter(Boolean) || [];

  // Query each bundleId for category — we'll use a single query with the first bundleId as a search
  // and paginate. Since we can't do per-ID queries with category, we'll use allowedByDeviceStatic
  // if the device serial is available.
  const deviceDetails = useQuery(
    api.devices.get.details,
    deviceId ? { device: deviceId as any } : "skip"
  );

  // Use allowedByDeviceStatic which returns apps with category info
  const allowedAppsStatic = useQuery(
    api.apps.list.allowedByDeviceStatic,
    appMode === "whitelist" && deviceDetails?.device?.serial
      ? { serial: deviceDetails.device.serial }
      : "skip"
  );

  /**
   * Filter sidebar items based on explicit appMode:
   * - whitelist: Settings, Whitelist only
   * - blacklist: Settings, Blacklist, Installed only  
   * - categories: All sections
   */
  const filteredSidebarItems = 
    appMode === "blacklist" 
      ? appsSidebarItems.filter(item => ["settings", "blacklist", "installed"].includes(item.id))
      : appMode === "whitelist"
      ? appsSidebarItems.filter(item => ["settings", "whitelist"].includes(item.id))
      : appsSidebarItems; // categories mode - show all

  // Handle app mode change from settings
  const handleAppModeChange = async (mode: AppMode) => {
    if (mode === "categories" && appMode === "whitelist" && deviceWhitelistedAppIds.length > 0) {
      // Build appId -> category map from the static allowed apps query
      const appIdToCategory: Record<string, string> = {};

      if (allowedAppsStatic) {
        for (const app of allowedAppsStatic) {
          if (app.id && app.category) {
            appIdToCategory[app.id as string] = app.category;
          }
        }
      }

      await setAppModeToCategoriesWithPrune(appIdToCategory);
    } else if (mode === "whitelist" && appMode === "categories") {
      // Intercept categories → whitelist: show the installed apps modal first
      setShowWhitelistImportModal(true);
    } else {
      await setAppMode(mode);
    }
  };

  const handleWhitelistImportConfirm = async (allowedApps: InstalledAppMeta[]) => {
    setShowWhitelistImportModal(false);
    await setAppModeToWhitelistWithImport(allowedApps);
  };

  const handleWhitelistImportCancel = () => {
    setShowWhitelistImportModal(false);
    // Do nothing — stay in categories mode
  };

  // Switch to appropriate section if current section is not available for the mode
  useEffect(() => {
    const availableIds = filteredSidebarItems.map(item => item.id);
    if (!availableIds.includes(activeAppsSection)) {
      // Switch to first available section
      setActiveAppsSection(filteredSidebarItems[0]?.id || "settings");
    }
  }, [appMode, activeAppsSection, filteredSidebarItems]);

  const renderContent = () => {
    if (activeAppsSection === "categories") {
      return <DeviceAppsCategoriesContent deviceId={deviceId || ''} />;
    }
    
    if (activeAppsSection === "settings") {
      return (
        <DeviceAppsSettingsContent
          deviceId={deviceId}
          appMode={appMode}
          onAppModeChange={handleAppModeChange}
        />
      );
    }
    
    return <DeviceAppsContent activeSection={activeAppsSection} deviceId={deviceId} />;
  };

  return (
    <ConvexErrorBoundary>
      <div className="flex h-full">
        <DeviceSidebar
          title="Apps"
          items={filteredSidebarItems}
          activeSection={activeAppsSection}
          onSectionChange={setActiveAppsSection}
        />
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto p-3">
            {renderContent()}
          </div>
        </div>
      </div>

      {showWhitelistImportModal && deviceId && (
        <InstalledAppsReviewModal
          open={showWhitelistImportModal}
          onOpenChange={(open) => { if (!open) handleWhitelistImportCancel(); }}
          deviceId={deviceId}
          pendingMode="whitelist"
          mode="modeSwitch"
          onConfirm={handleWhitelistImportConfirm}
          onCancel={handleWhitelistImportCancel}
        />
      )}
    </ConvexErrorBoundary>
  );
};
